// hooks/memos/mutations.js - Simple React Query mutations for memos
import { useMutation, useQueryClient } from '@tanstack/react-query';
import MemoAPI from 'memos/MemoAPI';
import ChatAPI from 'chat/ChatAPI';
import { excerptKeys } from '../excerpts/queries';
import { REMOVE_MEMO_MUTATION_KEY } from '../excerpts/settleMerge';
import { normalizeId } from '../utils/idNormalization';

/**
 * Remove memo from excerpt cache
 * @param {Object} queryClient - React Query client
 * @param {string|number} excerptId - The excerpt ID
 * @param {string|number} memoId - The memo ID to remove
 * @returns {Object|null} Previous excerpt data for rollback, or null if not found
 */
export const removeMemoFromExcerptCache = (queryClient, excerptId, memoId) => {
  if (!excerptId || !memoId) return null;
  
  const normalizedExcerptId = normalizeId(excerptId);
  const normalizedMemoId = normalizeId(memoId);
  
  // Get the current excerpt from cache
  const currentExcerpt = queryClient.getQueryData(excerptKeys.detail(normalizedExcerptId));
  
  if (currentExcerpt) {
    // Remove the memo from the excerpt
    const updatedExcerpt = {
      ...currentExcerpt,
      memos: (currentExcerpt.memos || []).filter(memo => 
        normalizeId(memo.id) !== normalizedMemoId
      )
    };
    
    // Update the cache
    queryClient.setQueryData(excerptKeys.detail(normalizedExcerptId), updatedExcerpt);
    
    // Return previous data for potential rollback
    return currentExcerpt;
  }
  
  return null;
};

/**
 * Mutation for creating a memo with optimistic updates
 */
export function useCreateMemo() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ excerptId, text }) => {
      return MemoAPI.createMemo(excerptId, text)
        .then(response => response.data);
    },
    
    // Optimistic update - add memo immediately
    onMutate: async ({ excerptId, text, userId }) => {
      const normalizedExcerptId = normalizeId(excerptId);
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: excerptKeys.detail(normalizedExcerptId) });
      
      // Snapshot the previous value
      const previousExcerpt = queryClient.getQueryData(excerptKeys.detail(normalizedExcerptId));
      
      // Optimistically add the new memo
      if (previousExcerpt) {
        const optimisticMemo = {
          id: `temp-${Date.now()}`,
          text,
          excerpt_id: excerptId,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          isPending: true // Mark as pending for visual indication
        };
        
        const updatedExcerpt = {
          ...previousExcerpt,
          memos: [...(previousExcerpt.memos || []), optimisticMemo]
        };
        
        queryClient.setQueryData(excerptKeys.detail(normalizedExcerptId), updatedExcerpt);
      }
      
      // Return context with snapshot for rollback
      return { previousExcerpt, excerptId: normalizedExcerptId };
    },
    
    // Replace optimistic memo with real one on success
    onSuccess: (response, { excerptId }) => {
      const normalizedExcerptId = normalizeId(excerptId);
      const newMemo = response.memo; // Extract memo from response
      
      const currentExcerpt = queryClient.getQueryData(excerptKeys.detail(normalizedExcerptId));
      
      if (currentExcerpt && newMemo) {
        // Remove the optimistic memo (isPending) AND any copy of the real
        // memo that already landed — a coding settle or excerpt broadcast
        // carrying it can reach the cache before this onSuccess, and an
        // unconditional append then rendered the memo twice (duplicate
        // React keys) with nothing to heal it.
        const updatedMemos = (currentExcerpt.memos || []).filter(
          memo => !memo.isPending && normalizeId(memo.id) !== normalizeId(newMemo.id)
        );
        const updatedExcerpt = {
          ...currentExcerpt,
          memos: [...updatedMemos, newMemo]
        };

        queryClient.setQueryData(excerptKeys.detail(normalizedExcerptId), updatedExcerpt);
      }
    },
    
    // Rollback on error
    onError: (error, variables, context) => {
      if (context?.previousExcerpt && context?.excerptId) {
        queryClient.setQueryData(
          excerptKeys.detail(context.excerptId),
          context.previousExcerpt
        );
      }
      console.error('Error creating memo:', error);
    }
  });
}

/**
 * Cache-only mutation for deleting a memo (for cable updates)
 */
export function useDeleteMemoFromCache() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ memoId, excerptId }) => {
      // This is a cache-only operation, no API call
      const previousExcerpt = removeMemoFromExcerptCache(queryClient, excerptId, memoId);
      return { memoId, excerptId, previousExcerpt };
    },
    
    onError: (error) => {
      console.error('Error deleting memo from cache:', error);
    }
  });
}

/**
 * Full mutation for deleting a memo (API call + cache update)
 */
export function useDeleteMemo() {
  const queryClient = useQueryClient();

  return useMutation({
    // The key lets the shared settle merge (settleMerge.js) see in-flight
    // memo deletions: an applying snapshot generated before this delete
    // committed still carries the memo and must not resurrect it.
    mutationKey: REMOVE_MEMO_MUTATION_KEY,
    mutationFn: async ({ memoId }) => {
      if (!memoId) {
        throw new Error('Memo ID is required');
      }
      
      return MemoAPI.deleteMemo(memoId)
        .then(response => response.data);
    },
    
    // Optimistic update - remove memo immediately
    onMutate: async ({ memoId, excerptId }) => {
      if (!excerptId) return;
      
      const normalizedExcerptId = normalizeId(excerptId);
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: excerptKeys.detail(normalizedExcerptId) });
      
      // Use the extracted cache update function
      const previousExcerpt = removeMemoFromExcerptCache(queryClient, excerptId, memoId);
      
      // Return context object with snapshot for rollback
      return { previousExcerpt, excerptId: normalizedExcerptId };
    },
    
    // On error, rollback to previous state
    onError: (error, variables, context) => {
      if (context?.previousExcerpt && context?.excerptId) {
        queryClient.setQueryData(
          excerptKeys.detail(context.excerptId), 
          context.previousExcerpt
        );
      }
      console.error('Error deleting memo:', error);
    },
    
    // Always refetch after success or error to ensure consistency
    onSettled: (data, error, { excerptId }) => {
      if (excerptId) {
        const normalizedExcerptId = normalizeId(excerptId);
        queryClient.invalidateQueries({ 
          queryKey: excerptKeys.detail(normalizedExcerptId) 
        });
      }
    }
  });
}

/**
 * Mutation for creating a memo chat (AI assistant memo with streaming)
 */
export function useCreateMemoChat() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ excerptId }) => {
      return ChatAPI.createMemoChat(excerptId)
        .then(response => response.data);
    },
    
    // Add the bot memo to cache on success
    onSuccess: (response, { excerptId }) => {
      const normalizedExcerptId = normalizeId(excerptId);
      const botMemo = response.memo; // Extract memo from response
      
      const currentExcerpt = queryClient.getQueryData(excerptKeys.detail(normalizedExcerptId));
      
      if (currentExcerpt && botMemo) {
        // Add the bot memo to the excerpt (id-deduped — a settle or
        // broadcast may have delivered it already, same as useCreateMemo).
        const updatedExcerpt = {
          ...currentExcerpt,
          memos: [
            ...(currentExcerpt.memos || []).filter(
              memo => normalizeId(memo.id) !== normalizeId(botMemo.id)
            ),
            botMemo
          ]
        };
        
        queryClient.setQueryData(excerptKeys.detail(normalizedExcerptId), updatedExcerpt);
      }
    },    
    onError: (error) => {
    }
  });
}

// Export all memo-related mutations
export const memoMutations = {
  useCreateMemo,
  useDeleteMemo,
  useDeleteMemoFromCache,
  useCreateMemoChat
};